/**
 * src/components/Financial/Dial90Chart.jsx
 * ミッドポイント分析・90度ダイアル 精密ビジュアル完全版
 * * 修正内容 [2026-02-14 v7]:
 * - Aries Point (♈) の表示色を signColors["♈"] (星座設定色) に変更
 * - Aries Point のシンボルをテキストスタイル ("♈\uFE0E") で統一
 * - ModalityStripChart へ signColors を伝播
 */

import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { styles as commonStyles } from "../../utils/styles.js";
import { PLANET_SYMBOLS, PLANETS, PLANET_NAMES_JP } from "../../utils/constants.js";
import * as Astro from "../../astronomy.js";
import {
  Printer,
  Copy,
  ChevronDown,
  ChevronUp,
  Zap,
  Activity,
  Compass,
  Layers,
  Info,
  Target,
  Download,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Droplet,
  Database,
  Calendar,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  MousePointer2,
} from "lucide-react";

// 解釈データのインポート
import { MIDPOINT_AXES, TRIGGER_PLANETS } from "../../utils/interpretations.js";

// =============================================================================
// 1. 定数定義
// =============================================================================

// シンボル拡張 (Aries Point対応)
const GET_SYMBOL = (id) => {
  if (id === "Aries") return "♈";
  return PLANET_SYMBOLS[id] || id;
};

const QUALITY_STYLE = {
  Cardinal: { bg: "#8b5cf6", label: "CARDINAL(活動宮)" },
  Fixed: { bg: "#c77a0a", label: "FIXED(固定宮)" },
  Mutable: { bg: "#27ae60", label: "MUTABLE(柔軟宮)" },
};

// ASC, MC, Aries を追加
const MAIN_BODIES = [...PLANETS, "NorthNode", "ASC", "MC", "Aries"];

const CLUSTER_TYPES = {
  TENSION: {
    id: "TENSION",
    label: "Volatility Cluster",
    jpLabel: "緊張型",
    icon: <AlertTriangle size={18} />,
    color: "#e74c3c",
    bg: "#fef2f2",
    border: "#fee2e2",
    description:
      "圧力・制限・衝突・恐怖。ボラティリティ上昇、急落・急騰、投げ、強制清算の予兆。",
    corePairs: [
      "Mars/Saturn",
      "Mars/Uranus",
      "Saturn/Uranus",
      "Mars/Pluto",
      "Saturn/Pluto",
    ],
    subPairs: ["Mercury/Saturn", "Moon/Saturn", "Mars/Sun"],
  },
  EXPANSION: {
    id: "EXPANSION",
    label: "Expansion Cluster",
    jpLabel: "拡張型",
    icon: <TrendingUp size={18} />,
    color: "#27ae60",
    bg: "#f0fdf4",
    border: "#dcfce7",
    description:
      "拡大・楽観・流動性。トレンド発生、バブル的動き。買われやすいがトレンドの過熱に注意。",
    corePairs: ["Jupiter/Venus", "Sun/Jupiter", "Moon/Jupiter"],
    subPairs: ["Mercury/Jupiter", "Jupiter/Uranus", "Sun/Venus"],
  },
  DISSOLUTION: {
    id: "DISSOLUTION",
    label: "Breakdown Cluster",
    jpLabel: "崩壊型",
    icon: <Droplet size={18} />,
    color: "#3498db",
    bg: "#f0f9ff",
    border: "#e0f2fe",
    description:
      "曖昧化・消失・崩壊。トレンド終了、信用崩壊、期待剥落。静かに壊れるタイプ。",
    corePairs: [
      "Neptune/Pluto",
      "Saturn/Neptune",
      "Sun/Neptune",
      "Moon/Neptune",
    ],
    subPairs: ["Venus/Neptune", "Mercury/Neptune", "Neptune/Uranus"],
  },
  TRANSFORMATION: {
    id: "TRANSFORMATION",
    label: "Regime Shift Cluster",
    jpLabel: "転換型",
    icon: <RefreshCw size={18} />,
    color: "#8e44ad",
    bg: "#faf5ff",
    border: "#f3e8ff",
    description:
      "破壊と再生・構造変化。市場レジームの転換、新テーマの誕生。投資家向け重要シグナル。",
    corePairs: ["Uranus/Pluto", "Saturn/Uranus", "Sun/Pluto", "Moon/Pluto"],
    subPairs: ["Mars/Uranus", "Jupiter/Pluto", "Mercury/Uranus"],
  },
};

const formatDMSPrecise = function (degrees) {
  if (degrees === null || degrees === undefined || isNaN(degrees)) return "0°00'00\"";
  const absDeg = Math.abs(degrees);
  const d = Math.floor(absDeg);
  const m = Math.floor((absDeg - d) * 60 + 0.00001); // 浮動小数点誤差回避
  const s = Math.floor(((absDeg - d) * 60 - m) * 60 + 0.01);
  return (
    d +
    "°" +
    m.toString().padStart(2, "0") +
    "'" +
    s.toString().padStart(2, "0") +
    '"'
  );
};

// =============================================================================
// 2. 内部コンポーネント
// =============================================================================

const MidpointRowItem = function (props) {
  const { isMobile, isDarkMode, planetColors, signColors, m, viewMode = "axis", natalPositions } = props;
  const [isOpen, setIsOpen] = useState(false);

  const interpretation = useMemo(
    function () {
      if (!m) return null;
      const pair1 = m.p1 + "/" + m.p2;
      const pair2 = m.p2 + "/" + m.p1;
      const axisInfo = MIDPOINT_AXES[pair1] || MIDPOINT_AXES[pair2];

      let triggerKey = "";
      if (viewMode === "axis") {
        triggerKey = m.tp;
      } else if (m.trigger && m.trigger.includes(".")) {
        triggerKey = m.trigger.split(".")[1];
      }

      const triggerInfo = TRIGGER_PLANETS[triggerKey];

      if (axisInfo) {
        return {
          axisName: axisInfo.name || "—",
          axisMeaning: axisInfo.meaning || "—",
          action: triggerInfo ? triggerInfo.action : "",
          effect: triggerInfo ? triggerInfo.effect : "",
        };
      }
      return null;
    },
    [m, viewMode],
  );

  const renderPlanet = function (id) {
    if (!id) return null;
    let baseId = id;
    let prefix = null;
    if (id.includes(".")) {
      [prefix, baseId] = id.split(".");
    }

    const color = planetColors[baseId] || (baseId === "Aries" ? (signColors["♈"] || "#ff0000") : (isDarkMode ? "#fff" : "#333"));
    const symbol = baseId === "Aries" ? "♈\uFE0E" : baseId === "Pointer" ? null : (PLANET_SYMBOLS[baseId] || baseId);

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "2px",
          overflow: "hidden",
        }}
      >
        {prefix && (
          <span style={{ fontSize: "9px", opacity: 0.7, marginRight: "1px" }}>{prefix}.</span>
        )}
        <span
          style={{
            fontSize: isMobile ? "18px" : "26px",
            color: baseId === "Pointer" ? "#e74c3c" : color,
            lineHeight: 1,
          }}
        >
          {baseId === "Pointer" ? <MousePointer2 size={isMobile ? 16 : 22} /> : symbol}
        </span>
        {!isMobile && (
          <span
            style={{
              fontSize: "13px",
              fontWeight: "bold",
              color: baseId === "Pointer" ? "#e74c3c" : (isDarkMode ? "#ffffff" : "#888"),
              marginLeft: "4px"
            }}
          >
            {baseId}
          </span>
        )}
      </div>
    );
  };

  const renderContactList = (contacts, type) => {
    if (!contacts || contacts.length === 0) return "—";
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px", justifyContent: "center" }}>
        {contacts.map(c => (
          <span key={c} title={c} style={{ fontSize: isMobile ? "14px" : "18px", color: planetColors[c] || "#888" }}>
            {PLANET_SYMBOLS[c] || c}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: "1px" }}>
      <div
        className="preserved-grid"
        onClick={function () {
          setIsOpen(!isOpen);
        }}
        style={{
          display: "grid",
          gridTemplateColumns: getMidpointGridLayout(isMobile, viewMode),
          padding: isMobile ? "8px 4px" : "10px 8px",
          alignItems: "center",
          background: props.isTight
            ? isDarkMode
              ? "rgba(237, 233, 209, 0.05)"
              : "#f9f9f5"
            : isDarkMode
              ? "transparent"
              : "#fff",
          borderBottom: "1px solid #f0f0f02d",
          cursor: "pointer",
          fontSize: isMobile ? "11px" : "12px",
          color: isDarkMode ? "#ffffff" : "inherit",
        }}
      >
        {viewMode === "contact" ? (
          <>
            {renderPlanet(m.tp)}
            <div style={{ display: "flex", alignItems: "center", gap: "2px", justifyContent: "center" }}>
              {renderPlanet(m.p1)}/ {renderPlanet(m.p2)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center", gap: "2px" }}>
              <span style={{ fontSize: "16px", color: (m.displaySign && signColors[m.displaySign.symbol]) || "#8b5cf6" }}>
                {(m.displaySign && m.displaySign.symbol + "\uFE0E") || "—\uFE0E"}
              </span>
              <span style={{ fontSize: "10px", opacity: 0.7 }}>{formatDMSPrecise(m.displayLon)}</span>
            </div>
            <div style={{ textAlign: "right", opacity: 0.8 }}>{m.mod90.toFixed(2)}°</div>
            <div style={{ textAlign: "center" }}>
              <span style={{
                fontSize: "10px",
                padding: "2px 4px",
                borderRadius: "4px",
                background: m.signal === "bullish" ? "#27ae60" : m.signal === "bearish" ? "#e74c3c" : "transparent",
                color: m.signal === "neutral" ? (isDarkMode ? "#fff" : "#333") : "#fff"
              }}>
                {m.signal.toUpperCase()}
              </span>
            </div>
            <div style={{ textAlign: "center" }}>
              {props.isInCluster && (
                <span style={{
                  fontSize: "9px",
                  fontWeight: "bold",
                  color: "#c2410c",
                  background: "rgba(194, 65, 12, 0.1)",
                  padding: "2px 4px",
                  borderRadius: "4px",
                  border: "1px solid rgba(194, 65, 12, 0.3)"
                }}>
                  {isMobile ? "CLS" : "CLUSTERED"}
                </span>
              )}
            </div>
            <div style={{ textAlign: "center" }}>
              <span style={{ fontSize: "8px", padding: "2px 5px", borderRadius: "10px", background: QUALITY_STYLE[m.modality]?.bg || "#ccc", color: "#fff" }}>
                {isMobile ? m.modality.substring(0, 1) : m.modality}
              </span>
            </div>
            <div style={{ textAlign: "right", fontWeight: "bold" }}>{m.orb.toFixed(2)}°</div>
          </>
        ) : viewMode === "axis" ? (
          <>
            {renderPlanet(m.p1)}
            {renderPlanet(m.p2)}
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center", gap: "2px" }}>
              <span style={{ fontSize: "16px", color: (m.displaySign && signColors[m.displaySign.symbol]) || "#8b5cf6" }}>
                {(m.displaySign && m.displaySign.symbol + "\uFE0E") || "—\uFE0E"}
              </span>
              <span style={{ fontSize: "10px", opacity: 0.7 }}>{formatDMSPrecise(m.displayLon)}</span>
            </div>
            <div style={{ textAlign: "right", opacity: 0.8 }}>{m.mod90.toFixed(2)}°</div>
            <div style={{ textAlign: "center" }}>{renderContactList(m.tContacts, "T")}</div>
            <div style={{ textAlign: "center" }}>{renderContactList(m.saContacts, "SA")}</div>

            <div style={{ textAlign: "center" }}>
              <span style={{ fontSize: "8px", padding: "2px 5px", borderRadius: "10px", background: QUALITY_STYLE[m.modality]?.bg || "#ccc", color: "#fff" }}>
                {isMobile ? m.modality.substring(0, 1) : m.modality}
              </span>
            </div>
            <div style={{ textAlign: "right", fontWeight: "bold" }}>{m.orb.toFixed(2)}°</div>
          </>
        ) : (
          <>
            {renderPlanet(m.trigger)}
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {renderPlanet(m.p1)}/ {renderPlanet(m.p2)}
            </div>
            <div style={{ textAlign: "right", fontWeight: "bold" }}>{m.orb.toFixed(2)}°</div>
            <div style={{ textAlign: "center" }}>
              <span style={{ fontSize: "10px", color: m.type === "Transit" ? "#3498db" : "#8e44ad" }}>{m.type === "Transit" ? "TR" : "SA"}</span>
            </div>
            {/* Interpretation is now moved to expanded view (accordion) */}
          </>
        )}
        <div style={{ textAlign: "right", color: "#cbd5e1" }}>
          {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </div>
      {isOpen && (
        <div
          style={{
            padding: isMobile ? "12px 15px" : "15px 25px",
            background: isDarkMode ? "rgba(255, 255, 255, 0.03)" : "#f8fbff",
            borderBottom: "1px solid #f0f0f0",
            fontSize: isMobile ? "11px" : "12.5px",
            lineHeight: "1.6",
            color: isDarkMode ? "rgba(255, 255, 255, 0.8)" : "inherit",
          }}
        >
          {interpretation ? (
            <div>
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: "8px",
                  color: isDarkMode ? "#ffffff" : "#2c2d96",
                  fontSize: isMobile ? "12px" : "14px",
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                  paddingBottom: "4px"
                }}
              >
                【{m.p1}/{m.p2}: {interpretation.axisName}】 {interpretation.axisMeaning}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.5fr", gap: "15px" }}>
                <div style={{ display: "grid", gap: "6px", fontSize: "11px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>P1 ({m.p1}):</span> <strong>{formatDMSPrecise(m.p1Lon || natalPositions[m.p1])}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>P2 ({m.p2}):</span> <strong>{formatDMSPrecise(m.p2Lon || natalPositions[m.p2])}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px dashed #ccc", paddingTop: "4px" }}>
                    <span>Midpoint (360°):</span> <strong>{formatDMSPrecise(m.displayLon)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Midpoint (90°):</span> <strong>{m.mod90.toFixed(2)}°</strong>
                  </div>
                </div>
                {interpretation.action && (
                  <div style={{ paddingLeft: "10px", borderLeft: "3px solid #c77a0a" }}>
                    <span style={{ fontWeight: "bold", color: "#c77a0a" }}>
                      {viewMode === "axis" ? m.tp : m.trigger} {interpretation.action}:
                    </span>{" "}
                    {interpretation.effect}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>解釈データがありません。</div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// 修正版コンポーネント: 90°ダイヤル ストリップチャート
// (モダリティラベル・星座アイコン復旧版)
// =============================================================================
const ModalityStripChart = ({
  transitDateTime,
  isDarkMode,
  planetColors,
  signColors,
  isMobile,
  isHeliocentric,
  natalPositions,
  // --- プロップス追加 ---
  initialSelectedTriggers = [],
  initialSelectedNatalBodies = [],
  orbFilter = 1.5,
  userLocation = null,
}) => {
  const chartRef = useRef(null);
  // 初期値をプロップスから取得するように変更 (リストと同期)
  const [selectedTriggers, setSelectedTriggers] = useState(() => {
    if (initialSelectedTriggers.length > 0) return initialSelectedTriggers;
    const saved = localStorage.getItem("astronica_dial90_selected_triggers");
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedNatalBodies, setSelectedNatalBodies] = useState(() => {
    if (initialSelectedNatalBodies.length > 0) return initialSelectedNatalBodies;
    const saved = localStorage.getItem("astronica_dial90_selected_natal_bodies");
    return saved ? JSON.parse(saved) : [];
  });

  // 保存用Effect
  React.useEffect(() => {
    localStorage.setItem("astronica_dial90_selected_triggers", JSON.stringify(selectedTriggers));
  }, [selectedTriggers]);

  React.useEffect(() => {
    localStorage.setItem("astronica_dial90_selected_natal_bodies", JSON.stringify(selectedNatalBodies));
  }, [selectedNatalBodies]);

  // 座標系に応じて対象リストを動的に変更
  const targetPlanets = useMemo(() => {
    if (isHeliocentric) {
      return [
        "Earth",
        "Mercury",
        "Venus",
        "Mars",
        "Jupiter",
        "Saturn",
        "Uranus",
        "Neptune",
        "Pluto",
        "Aries",
      ];
    }
    return [
      "Sun",
      "Moon",
      "Mercury",
      "Venus",
      "Mars",
      "Jupiter",
      "Saturn",
      "Uranus",
      "Neptune",
      "Pluto",
      "NorthNode",
      "ASC",
      "MC",
      "Aries",
    ];
  }, [isHeliocentric]);

  const toggleTrigger = (planet) => {
    setSelectedTriggers((prev) =>
      prev.includes(planet)
        ? prev.filter((p) => p !== planet)
        : [...prev, planet],
    );
  };

  const toggleNatalBody = (planet) => {
    setSelectedNatalBodies((prev) =>
      prev.includes(planet)
        ? prev.filter((p) => p !== planet)
        : [...prev, planet],
    );
  };

  // ヘルパー: 色取得ロジック
  const getBodyColor = (id) => {
    if (id === "Aries") return signColors["♈"] || "#ff0000";
    return planetColors[id] || "#888";
  };

  // ---------------------------------------------------------------------------
  // データ計算
  // ---------------------------------------------------------------------------

  const chartData = useMemo(() => {
    if (!transitDateTime) return { points: [], dates: [] };

    let startDate;
    const dateMatch = transitDateTime.match(
      /(\d{4})[\/\-\.\s](\d{1,2})[\/\-\.\s](\d{1,2})/,
    );
    if (dateMatch) {
      startDate = new Date(
        parseInt(dateMatch[1]),
        parseInt(dateMatch[2]) - 1,
        parseInt(dateMatch[3]),
      );
    } else {
      startDate = new Date();
    }

    const data = [];
    const dates = [];
    const days = 7;
    const step = 0.1;

    for (let i = 0; i <= days; i += step) {
      const curDate = new Date(startDate.getTime() + i * 86400000);
      const dayData = {};

      targetPlanets.forEach((p) => {
        if (selectedTriggers.length > 0 && !selectedTriggers.includes(p))
          return;

        if (p === "Aries") {
          dayData[p] = 0;
        } else {
          // 位置情報を渡して ASC/MC 等を正しく取得
          const lon = Astro.getAnyPlanetLongitude(
            p,
            curDate,
            isHeliocentric,
            userLocation?.latitude,
            userLocation?.longitude
          );
          if (lon !== null) {
            dayData[p] = lon % 90;
          }
        }
      });

      data.push(dayData);
      if (Math.abs(i - Math.round(i)) < 0.001) {
        dates.push({
          index: i / step,
          label: `${curDate.getMonth() + 1}/${curDate.getDate()}`,
        });
      }
    }
    return { points: data, dates: dates };
  }, [transitDateTime, isHeliocentric, selectedTriggers, targetPlanets, userLocation]);

  const natalMidpoints = useMemo(() => {
    if (!natalPositions) return [];
    if (selectedNatalBodies.length === 0) return [];

    const bodies = targetPlanets;
    const mps = [];

    for (let i = 0; i < bodies.length; i++) {
      for (let j = i + 1; j < bodies.length; j++) {
        const p1 = bodies[i];
        const p2 = bodies[j];

        if (p1 === p2) continue;

        if (
          !selectedNatalBodies.includes(p1) ||
          !selectedNatalBodies.includes(p2)
        ) {
          continue;
        }

        const pos1 = p1 === "Aries" ? 0 : natalPositions[p1];
        const pos2 = p2 === "Aries" ? 0 : natalPositions[p2];

        if (pos1 !== undefined && pos2 !== undefined) {
          let mid = Astro.calculateMidpoint(pos1, pos2) % 90;
          mps.push({ p1, p2, mid });
        }
      }
    }
    return mps;
  }, [natalPositions, selectedNatalBodies, targetPlanets]);

  const activeContacts = useMemo(() => {
    if (
      !chartData.points.length ||
      !natalMidpoints.length ||
      selectedTriggers.length === 0
    )
      return [];

    const contacts = [];
    natalMidpoints.forEach((mp) => {
      selectedTriggers.forEach((tp) => {
        let inZone = false;
        let zoneStartIndex = -1;
        let minOrb = 100;
        let minIndex = -1;

        chartData.points.forEach((pt, i) => {
          const tVal = pt[tp];
          if (tVal === undefined) return;
          let diff = Math.abs(tVal - mp.mid);
          if (diff > 45) diff = 90 - diff;

          // orbFilter プロップスを使用するように変更
          if (diff <= orbFilter) {
            if (!inZone) {
              inZone = true;
              zoneStartIndex = i;
              minOrb = diff;
              minIndex = i;
            } else {
              if (diff < minOrb) {
                minOrb = diff;
                minIndex = i;
              }
            }
          } else {
            if (inZone) {
              pushContact(
                contacts,
                mp,
                tp,
                minIndex,
                minOrb,
                zoneStartIndex,
                i - 1,
              );
              inZone = false;
            }
          }
        });
        if (inZone) {
          pushContact(
            contacts,
            mp,
            tp,
            minIndex,
            minOrb,
            zoneStartIndex,
            chartData.points.length - 1,
          );
        }
      });
    });
    return contacts;
  }, [chartData, natalMidpoints, selectedTriggers]);

  function pushContact(list, mp, tp, peakIndex, minOrb, startIdx, endIdx) {
    list.push({
      mp: mp,
      trigger: tp,
      dateIndex: peakIndex,
      startIndex: startIdx,
      endIndex: endIdx,
      orb: minOrb,
    });
  }

  const visibleMidpoints = useMemo(() => {
    const map = new Map();
    activeContacts.forEach((c) => {
      const key = `${c.mp.p1}-${c.mp.p2}`;
      if (!map.has(key)) {
        map.set(key, c.mp);
      }
    });
    return Array.from(map.values());
  }, [activeContacts]);

  const width = 1000;
  const height = 400;
  const padding = { top: 20, right: 100, bottom: 40, left: 60 };
  const graphWidth = width - padding.left - padding.right;
  const graphHeight = height - padding.top - padding.bottom;
  const daysCount =
    chartData.points.length > 1 ? chartData.points.length - 1 : 1;

  const getX = (index) => padding.left + (index / daysCount) * graphWidth;
  const getY = (val) => padding.top + graphHeight - (val / 90) * graphHeight;

  const generatePath = (planet) => {
    let d = "";
    let prevVal = null;
    chartData.points.forEach((pt, i) => {
      const val = pt[planet];
      if (val === undefined) return;
      const x = getX(i);
      const y = getY(val);
      if (i === 0) {
        d += `M ${x} ${y}`;
      } else {
        if (Math.abs(val - prevVal) > 70) {
          d += ` M ${x} ${y}`;
        } else {
          d += ` L ${x} ${y}`;
        }
      }
      prevVal = val;
    });
    return d;
  };

  const renderPlanetButtons = (selectedList, toggleFunc, label) => (
    <div
      style={{
        padding: "10px 15px",
        borderBottom: isDarkMode
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid #f0f0f0",
        background: isDarkMode ? "transparent" : "#fff",
      }}
    >
      <div
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          marginBottom: "8px",
          color: isDarkMode ? "#aaa" : "#64748b",
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {targetPlanets.map((planet) => {
          const isSelected = selectedList.includes(planet);
          const pColor = getBodyColor(planet);

          return (
            <button
              key={planet}
              onClick={() => toggleFunc(planet)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                borderRadius: "20px",
                border: isSelected
                  ? `1px solid ${pColor}`
                  : isDarkMode
                    ? "1px solid rgba(255,255,255,0.2)"
                    : "1px solid #e2e8f0",
                background: isSelected
                  ? isDarkMode
                    ? "rgba(255,255,255,0.1)"
                    : "#fff"
                  : "transparent",
                color: isSelected ? pColor : isDarkMode ? "#aaa" : "#64748b",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: isSelected ? "bold" : "normal",
                transition: "all 0.2s",
                boxShadow: isSelected ? `0 2px 5px ${pColor}33` : "none",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "normal",
                  lineHeight: 1,
                }}
              >
                {planet === "Aries" ? "♈\uFE0E" : PLANET_SYMBOLS[planet] || planet}
              </span>
              <span>{planet}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      style={{
        gridColumn: "span 12",
        marginBottom: "20px",
        ...commonStyles.dynamicBlockCard(isDarkMode, isMobile),
      }}
    >
      <div
        style={{
          ...commonStyles.dynamicBlockTitle(isDarkMode, isMobile),
          color: isDarkMode ? "#fff" : "rgb(30, 41, 59)",
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Calendar size={20} />
          <span>90° ダイヤル週間カレンダー(ミッドポイント活性化)</span>
        </div>
        <div style={{ fontSize: "11px", opacity: 0.7 }}>
          Start: {transitDateTime} (7 Days)
        </div>
      </div>

      {renderPlanetButtons(
        selectedTriggers,
        toggleTrigger,
        "① 分析対象トリガー天体の選択 (経過円)",
      )}
      {renderPlanetButtons(
        selectedNatalBodies,
        toggleNatalBody,
        "② ミッドポイント構成軸の選択 (出生円)",
      )}

      <div
        style={{
          overflowX: "auto",
          padding: "10px",
          background: isDarkMode ? "#1a1b1e" : "#fff",
          borderRadius: "8px",
        }}
      >
        <svg
          ref={chartRef}
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: "100%", minWidth: "600px", height: "auto" }}
        >
          {/* 背景 (Zones) */}
          <rect
            x={padding.left}
            y={getY(30)}
            width={graphWidth}
            height={getY(0) - getY(30)}
            fill={isDarkMode ? "#2c2d96" : "#e0e7ff"}
            opacity={0.1}
          />
          <rect
            x={padding.left}
            y={getY(60)}
            width={graphWidth}
            height={getY(30) - getY(60)}
            fill={isDarkMode ? "#c77a0a" : "#fef3c7"}
            opacity={0.1}
          />
          <rect
            x={padding.left}
            y={getY(90)}
            width={graphWidth}
            height={getY(60) - getY(90)}
            fill={isDarkMode ? "#27ae60" : "#dcfce7"}
            opacity={0.1}
          />

          {/* Orb侵入期間のハイライト */}
          {activeContacts.map((c, i) => {
            const startX = getX(c.startIndex);
            const endX = getX(c.endIndex);
            const y = getY(c.mp.mid);
            const color = getBodyColor(c.trigger);
            const zoneWidth = Math.max(endX - startX, 2);

            return (
              <rect
                key={`zone-bg-${i}`}
                x={startX}
                y={y - 15}
                width={zoneWidth}
                height={30}
                fill={color}
                opacity={isDarkMode ? 0.15 : 0.1}
                rx={4}
              />
            );
          })}

          {/* グリッド線 */}
          {[0, 30, 60, 90].map((val) => (
            <g key={`grid-y-${val}`}>
              <line
                x1={padding.left}
                y1={getY(val)}
                x2={width - padding.right}
                y2={getY(val)}
                stroke={isDarkMode ? "#444" : "#e2e8f0"}
                strokeWidth={val % 30 === 0 ? 1.5 : 0.5}
              />
              <text
                x={padding.left - 5}
                y={getY(val) + 4}
                fontSize="10"
                textAnchor="end"
                fill={isDarkMode ? "#aaa" : "#64748b"}
              >
                {val}°
              </text>
            </g>
          ))}

          {/* ★復旧: モダリティラベル & 星座アイコン */}
          <text
            x={width - padding.right + 10}
            y={getY(15)}
            textAnchor="start"
            fontSize="12"
            fontWeight="bold"
            fill={isDarkMode ? "#aaa" : "#64748b"}
            opacity="0.8"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            Cardinal ♈︎♋︎♎︎♑︎
          </text>
          <text
            x={width - padding.right + 10}
            y={getY(45)}
            textAnchor="start"
            fontSize="12"
            fontWeight="bold"
            fill={isDarkMode ? "#aaa" : "#64748b"}
            opacity="0.8"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            Fixed ♉︎♌︎♏︎♒︎
          </text>
          <text
            x={width - padding.right + 10}
            y={getY(75)}
            textAnchor="start"
            fontSize="12"
            fontWeight="bold"
            fill={isDarkMode ? "#aaa" : "#64748b"}
            opacity="0.8"
            style={{ fontFamily: "Arial, sans-serif" }}
          >
            Mutable ♊︎♍︎♐︎♓︎
          </text>

          {/* X軸グリッド */}
          {chartData.dates.map((d, i) => (
            <g key={`grid-x-${i}`}>
              <line
                x1={getX(d.index)}
                y1={padding.top}
                x2={getX(d.index)}
                y2={height - padding.bottom}
                stroke={isDarkMode ? "#555" : "#cbd5e1"}
                strokeWidth={1}
                strokeDasharray="3 3"
              />
              <text
                x={getX(d.index)}
                y={height - 15}
                fontSize="10"
                textAnchor="middle"
                fill={isDarkMode ? "#aaa" : "#64748b"}
              >
                {d.label}
              </text>
            </g>
          ))}

          {/* ネイタルミッドポイント線 */}
          {visibleMidpoints.map((mp, i) => {
            const y = getY(mp.mid);
            const sym1 = mp.p1 === "Aries" ? "♈" : PLANET_SYMBOLS[mp.p1] || mp.p1;
            const sym2 = mp.p2 === "Aries" ? "♈" : PLANET_SYMBOLS[mp.p2] || mp.p2;

            // 自己ミッドポイントは本来除外されているが、安全のためフィルタリング
            if (mp.p1 === mp.p2) return null;
            const label = `${sym1}/${sym2}`;
            const color = getBodyColor(mp.p1);

            return (
              <g key={`mp-line-${i}`}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={width - padding.right}
                  y2={y}
                  stroke={color}
                  strokeWidth={0.5}
                  strokeDasharray="2 2"
                  opacity={0.5}
                />
                <text
                  x={padding.left - 5}
                  y={y + 3}
                  fontSize="9"
                  textAnchor="end"
                  fill={color}
                  fontWeight="bold"
                  opacity={0.8}
                >
                  {label}
                </text>
              </g>
            );
          })}

          {/* 惑星パス */}
          {targetPlanets.map((p) => {
            if (!selectedTriggers.includes(p)) return null;

            const color = getBodyColor(p);
            const opacity = p === "Moon" ? 0.5 : 0.8;
            const strokeWidth = p === "Moon" ? 1 : 2;

            return (
              <path
                key={`path-${p}`}
                d={generatePath(p)}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                opacity={opacity}
              />
            );
          })}

          {/* 接触マーカー */}
          {activeContacts.map((c, i) => {
            const cx = getX(c.dateIndex);
            const cy = getY(c.mp.mid);
            const color = getBodyColor(c.trigger);

            return (
              <g key={`contact-marker-${i}`}>
                <circle
                  cx={cx}
                  cy={cy}
                  r="3"
                  fill={color}
                  stroke={isDarkMode ? "#000" : "#fff"}
                  strokeWidth="1"
                  opacity="0.9"
                />
                <text
                  x={cx}
                  y={cy - 6}
                  fontSize="10"
                  textAnchor="middle"
                  fill={color}
                  fontWeight="bold"
                >
                  {c.trigger === "Aries" ? "♈" : PLANET_SYMBOLS[c.trigger] || c.trigger}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

// getMidpointGridLayout は既存のまま、viewModeで拡張
const getMidpointGridLayout = function (isMobile, viewMode = "axis") {
  if (viewMode === "axis") {
    return isMobile
      ? "minmax(35px, 1fr) minmax(35px, 1fr) minmax(50px, 1.3fr) minmax(45px, 1.2fr) 25px 25px 35px 38px 25px"
      : "1fr 1fr 1.4fr 1.2fr 0.7fr 0.7fr 0.9fr 0.9fr 30px";
  } else if (viewMode === "contact") {
    // Contact List View (Trigger, Midpoint, Midpoint(360), 90Deg, Judgment, Cluster, Quality, Orb)
    return isMobile
      ? "minmax(35px, 1.2fr) minmax(70px, 1.5fr) minmax(45px, 1.2fr) minmax(40px, 1.2fr) minmax(50px, 1.2fr) minmax(35px, 1fr) minmax(25px, 0.8fr) minmax(35px, 1fr) 25px"
      : "0.8fr 1.5fr 1fr 0.8fr 1fr 0.8fr 0.7fr 0.7fr 30px";
  } else {
    // Planet View (Trigger, Midpoint, Orb, Type)
    return isMobile
      ? "minmax(50px, 1fr) minmax(70px, 1.5fr) 45px 35px 30px"
      : "1.2fr 1.8fr 1fr 1fr 30px";
  }
};

// =============================================================================
// メインコンポーネント
// =============================================================================

export default function Dial90Chart(props) {
  var isDarkMode = props.isDarkMode;
  const midpoints = props.midpoints || [];
  const transitPositions = props.transitPositions || {};
  const natalPositions = props.natalPositions || {};
  const planetColors = props.planetColors || {};
  const signColors = props.signColors || {}; // ★追加
  const assetName = props.assetName;
  const natalDateTime = props.natalDateTime;
  const transitDateTime = props.transitDateTime;
  const isHeliocentric = props.isHeliocentric || false;
  const isSidereal = props.isSidereal || false;
  const transitDateObj = props.transitDateObj || new Date();
  const natalDateObj = props.natalDateObj;

  const svgRef = useRef(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;

  // --- ポインター機能の追加 ---
  const [pointerDegree, setPointerDegree] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: "orb", direction: "asc" });
  const [viewMode, setViewMode] = useState("axis"); // "axis" | "planet"
  const [selectedTrigger, setSelectedTrigger] = useState("all"); // "all" | id

  // キーボードイベントの登録
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 90度ダイヤルが表示されているセクションがアクティブな場合のみ反応させるのが理想だが、
      // ここではグローバルにリッスンし、他の入力要素にフォーカスがない場合のみ動作させる。
      if (document.activeElement.tagName === "INPUT" || document.activeElement.tagName === "TEXTAREA") {
        return;
      }

      if (e.key === "ArrowRight") {
        setPointerDegree((prev) => (prev + 0.1) % 90);
        e.preventDefault();
      } else if (e.key === "ArrowLeft") {
        setPointerDegree((prev) => (prev - 0.1 + 90) % 90);
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const centerX = 250;
  const centerY = 300;
  const outerRadius = 210;
  const innerRadius = 150;

  // ヘルパー: 色取得ロジック（メインチャート用）
  const getBodyColor = (id) => {
    if (id === "Aries") return signColors["♈"] || "#ff0000";
    return planetColors[id] || (id === "Aries" ? "#ff0000" : "#8b5cf6");
  };

  const degToCoord = useCallback(function (deg90, r) {
    const angle360 = (deg90 % 90) * 4;
    const rad = ((angle360 - 90) * Math.PI) / 180;
    return { x: centerX + r * Math.cos(rad), y: centerY + r * Math.sin(rad) };
  }, [centerX, centerY]);

  // 重なり防止のためのクラスタリングロジック
  const getClusteredBodies = useCallback(
    function (positions, baseRadius, innerBodies = false) {
      if (!positions) return [];
      const bodies = MAIN_BODIES.map(function (id) {
        return { id: id, lon: positions[id] };
      })
        .filter(function (b) {
          return b.lon !== undefined && b.lon !== null;
        })
        .sort(function (a, b) {
          return (a.lon % 90) - (b.lon % 90);
        });

      if (bodies.length === 0) return [];

      const clusters = [];
      let currentCluster = [];
      bodies.forEach(function (body) {
        if (currentCluster.length === 0) {
          currentCluster.push(body);
        } else {
          const prev = currentCluster[currentCluster.length - 1];
          let diff = Math.abs((body.lon % 90) - (prev.lon % 90));
          if (diff > 45) diff = 90 - diff;

          // 90度ダイヤル上での近接判定 (2.5度以内)
          if (diff <= 2.5) {
            currentCluster.push(body);
          } else {
            clusters.push(currentCluster);
            currentCluster = [body];
          }
        }
      });
      if (currentCluster.length > 0) clusters.push(currentCluster);

      const results = [];
      clusters.forEach(function (cluster) {
        const centerOffset = (cluster.length - 1) / 2;
        cluster.forEach(function (body, index) {
          // 重なりを避けるために半径をずらす
          const offset = (index - centerOffset) * (innerBodies ? -16 : 18);
          results.push(
            Object.assign({}, body, {
              displayRadius: baseRadius + offset,
              exactLon: body.lon,
            }),
          );
        });
      });
      return results;
    },
    [],
  );

  const natalBodies = useMemo(
    function () {
      const pos = Object.assign({ Aries: 0 }, natalPositions);
      return getClusteredBodies(pos, innerRadius, true);
    },
    [natalPositions, innerRadius, getClusteredBodies],
  );

  const transitBodies = useMemo(
    function () {
      const pos = Object.assign({ Aries: 0 }, transitPositions);
      return getClusteredBodies(pos, outerRadius + 22, false);
    },
    [transitPositions, outerRadius, getClusteredBodies],
  );

  // --- SA天体の位置計算 ---
  const saPositions = useMemo(() => {
    if (!natalPositions || !transitDateObj || !natalDateObj) return {};
    const saPos = Astro.getSolarArcPositions(natalDateObj, transitDateObj, natalPositions);
    return Object.assign({ Aries: 0 }, saPos);
  }, [natalPositions, transitDateObj, natalDateObj]);

  const saBodies = useMemo(() => {
    return getClusteredBodies(saPositions, outerRadius + 44, false);
  }, [saPositions, outerRadius, getClusteredBodies]);

  // クラスタ計算などは省略せずにそのまま記載
  const clusters = useMemo(
    function () {
      if (midpoints.length === 0) return [];
      const sorted = midpoints.slice().sort(function (a, b) {
        return a.mod90 - b.mod90;
      });
      const results = [];
      let temp = [];
      for (let i = 0; i < sorted.length; i++) {
        if (temp.length === 0) {
          temp.push(sorted[i]);
        } else {
          if (Math.abs(sorted[i].mod90 - temp[0].mod90) <= 0.8) {
            temp.push(sorted[i]);
          } else {
            if (temp.length >= 3)
              results.push({
                avgDeg:
                  temp.reduce(function (s, m) {
                    return s + m.mod90;
                  }, 0) / temp.length,
                members: temp,
              });
            temp = [sorted[i]];
          }
        }
      }
      if (temp.length >= 3)
        results.push({
          avgDeg:
            temp.reduce(function (s, m) {
              return s + m.mod90;
            }, 0) / temp.length,
          members: temp,
        });
      return results;
    },
    [midpoints],
  );

  const clusterClassifications = useMemo(
    function () {
      if (!clusters || clusters.length === 0) return [];
      return clusters
        .map(function (cluster, idx) {
          const scores = {
            TENSION: 0,
            EXPANSION: 0,
            DISSOLUTION: 0,
            TRANSFORMATION: 0,
          };
          cluster.members.forEach(function (m) {
            const sortedPair = [m.p1, m.p2].sort().join("/");
            Object.keys(CLUSTER_TYPES).forEach(function (typeKey) {
              const typeDef = CLUSTER_TYPES[typeKey];
              if (typeDef.corePairs.includes(sortedPair)) {
                scores[typeKey] += 2;
              } else if (typeDef.subPairs.includes(sortedPair)) {
                scores[typeKey] += 1;
              }
            });
          });
          const sortedKeys = Object.keys(scores)
            .filter((key) => scores[key] > 0)
            .sort((a, b) => scores[b] - scores[a]);
          if (sortedKeys.length === 0) return null;
          const primaryKey = sortedKeys[0];
          return {
            id: idx,
            avgDeg: cluster.avgDeg,
            displayType: CLUSTER_TYPES[primaryKey],
            status: scores[primaryKey] >= 4 ? "High" : "Medium",
            memberCount: cluster.members.length,
          };
        })
        .filter((c) => c !== null);
    },
    [clusters],
  );

  const energyAnalysis = useMemo(
    function () {
      if (midpoints.length === 0)
        return {
          total: 0,
          intensity: "なし",
          phase: "静観",
          dominantQuality: "なし",
          mainTrigger: "なし",
        };
      const score = Math.round(
        midpoints.reduce(function (s, m) {
          return s + (m.orb <= 0.3 ? 3 : m.orb <= 0.7 ? 1.5 : 0.5);
        }, 0) +
        clusters.length * 15,
      );
      const qCount = { Cardinal: 0, Fixed: 0, Mutable: 0 };
      midpoints.forEach(function (m) {
        qCount[m.modality]++;
      });
      const dominant = Object.keys(qCount).sort(function (a, b) {
        return qCount[b] - qCount[a];
      })[0];
      const trHits = {};
      midpoints.forEach(function (m) {
        trHits[m.tp] = (trHits[m.tp] || 0) + 1;
      });
      const mainTrigger = Object.keys(trHits).sort(function (a, b) {
        return trHits[b] - trHits[a];
      })[0];
      return {
        total: score,
        intensity:
          score >= 100
            ? "最大 (臨界)"
            : score >= 75
              ? "高 (警戒)"
              : "中 (蓄積)",
        phase:
          score >= 75 ? "トレンド反転警戒ゾーン" : "エネルギー蓄積フェーズ",
        dominantQuality:
          dominant +
          " (" +
          (dominant === "Fixed"
            ? "固定宮"
            : dominant === "Cardinal"
              ? "活動宮"
              : "柔軟宮") +
          ")",
        mainTrigger: mainTrigger,
      };
    },
    [midpoints, clusters],
  );

  const filteredMidpoints = useMemo(
    function () {
      const list = midpoints
        .map(function (m) {
          const p1Pos = m.p1 === "Aries" ? 0 : natalPositions[m.p1];
          const p2Pos = m.p2 === "Aries" ? 0 : natalPositions[m.p2];

          if (p1Pos === undefined || p2Pos === undefined) {
            return null;
          }

          const baseMid = Astro.calculateMidpoint(p1Pos, p2Pos);
          const mid90 = baseMid % 90;

          const triggerPos = m.tp === "Aries" ? 0 : transitPositions[m.tp];
          if (triggerPos === undefined) return null;

          return Object.assign({}, m, {
            p1Lon: p1Pos,
            p2Lon: p2Pos,
            tLon: triggerPos,
            displayLon: baseMid,
            displaySign: Astro.getZodiacSign(baseMid, isSidereal, transitDateObj),
            mod90: mid90,
            modality: Astro.getModality ? Astro.getModality(mid90) : "Unknown",
          });
        })
        .filter((m) => m !== null);

      return list.sort(function (a, b) {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        if (valA === undefined) valA = 0;
        if (valB === undefined) valB = 0;

        if (sortConfig.key === "displayLon" || sortConfig.key === "mod90") {
          valA = valA % 90;
          valB = valB % 90;
        }

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    },
    [midpoints, natalPositions, transitPositions, isSidereal, transitDateObj, sortConfig],
  );

  // ポインター軸（45度先）のミッドポイント計算
  // 修正 [2026-03-11]: T.Contact, SA.Contact, Intensity を追加
  const pointerSelectedMidpoints = useMemo(() => {
    const target = (pointerDegree + 45) % 90;
    const orbLimit = 1.0;
    // サウスノードとハウスカスプを除外
    const planetsForMid = Array.from(new Set([...PLANETS, "NorthNode", "ASC", "MC", "Aries"]));
    const results = [];

    // トランジットとSAの 90度モジュロ位置を準備 (サウスノードとハウスカスプを除外)
    const tMod90 = Object.entries(transitPositions)
      .filter(([id]) => id !== "SouthNode" && !id.startsWith("H"))
      .map(([id, lon]) => ({ id, m90: lon % 90 }));
    const saMod90 = Object.entries(saPositions)
      .filter(([id]) => id !== "SouthNode" && !id.startsWith("H"))
      .map(([id, lon]) => ({ id, m90: lon % 90 }));

    for (let i = 0; i < planetsForMid.length; i++) {
      for (let j = i + 1; j < planetsForMid.length; j++) {
        const p1 = planetsForMid[i];
        const p2 = planetsForMid[j];

        const p1Pos = p1 === "Aries" ? 0 : natalPositions[p1];
        const p2Pos = p2 === "Aries" ? 0 : natalPositions[p2];

        if (p1Pos === undefined || p2Pos === undefined) continue;

        const baseMid = Astro.calculateMidpoint(p1Pos, p2Pos);
        const mid90 = baseMid % 90;

        let diff = Math.abs(mid90 - target);
        if (diff > 45) diff = 90 - diff;

        if (diff <= orbLimit) {
          // トランジットコンタクト判定
          const tContacts = tMod90.filter(t => {
            let d = Math.abs(t.m90 - mid90);
            if (d > 45) d = 90 - d;
            return d <= 1.0;
          }).map(t => t.id);

          // SAコンタクト判定
          const saContacts = saMod90.filter(s => {
            let d = Math.abs(s.m90 - mid90);
            if (d > 45) d = 90 - d;
            return d <= 1.0;
          }).map(s => s.id);

          const intensity = 1 + tContacts.length + saContacts.length;

          results.push({
            p1: p1,
            p2: p2,
            p1Lon: p1Pos,
            p2Lon: p2Pos,
            tp: "Pointer",
            tLon: target,
            displayLon: baseMid,
            displaySign: Astro.getZodiacSign(baseMid, isSidereal, transitDateObj),
            orb: diff, // Axis Orb
            modality: Astro.getModality ? Astro.getModality(mid90) : "Unknown",
            mod90: mid90,
            signal: "neutral",
            tContacts,
            saContacts,
            intensity
          });
        }
      }
    }

    return results.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (valA === undefined) valA = 0;
      if (valB === undefined) valB = 0;

      if (sortConfig.key === "displayLon" || sortConfig.key === "mod90") {
        valA = valA % 90;
        valB = valB % 90;
      }

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [pointerDegree, natalPositions, transitPositions, saPositions, isSidereal, transitDateObj, sortConfig]);

  // --- View by Planet (逆引き) ロジック ---
  const planetTriggeredMidpoints = useMemo(() => {
    const results = [];
    // サウスノードとハウスカスプを除外
    const planetsForMid = Array.from(new Set([...PLANETS, "NorthNode", "ASC", "MC", "Aries"]));

    // 全ネイタル中点を事前計算
    const allNatalMidpoints = [];
    for (let i = 0; i < planetsForMid.length; i++) {
      for (let j = i + 1; j < planetsForMid.length; j++) {
        const p1 = planetsForMid[i];
        const p2 = planetsForMid[j];
        const p1Pos = p1 === "Aries" ? 0 : natalPositions[p1];
        const p2Pos = p2 === "Aries" ? 0 : natalPositions[p2];
        if (p1Pos !== undefined && p2Pos !== undefined) {
          const mid = Astro.calculateMidpoint(p1Pos, p2Pos);
          allNatalMidpoints.push({ p1, p2, lon: mid, m90: mid % 90 });
        }
      }
    }

    // トリガー対象: トランジット天体 + SA天体 (サウスノード・ハウスカスプ除外) + ポインター
    const triggers = [
      ...Object.entries(transitPositions)
        .filter(([id]) => id !== "SouthNode" && !id.startsWith("H"))
        .map(([id, lon]) => ({ id: "T." + id, name: id, m90: lon % 90, type: "Transit" })),
      ...Object.entries(saPositions)
        .filter(([id]) => id !== "SouthNode" && !id.startsWith("H"))
        .map(([id, lon]) => ({ id: "SA." + id, name: id, m90: lon % 90, type: "SA" })),
      { id: "Pointer", name: "Pointer", m90: (pointerDegree + 45) % 90, type: "Pointer" }
    ];

    triggers.forEach(trig => {
      // トリガー選択によるフィルタリング
      if (selectedTrigger !== "all" && selectedTrigger !== trig.id) return;

      allNatalMidpoints.forEach(mp => {
        let diff = Math.abs(trig.m90 - mp.m90);
        if (diff > 45) diff = 90 - diff;

        if (diff <= 1.0) {
          results.push({
            trigger: trig.id,
            p1: mp.p1,
            p2: mp.p2,
            displayLon: mp.lon,
            mod90: mp.m90,
            orb: diff,
            type: trig.type
          });
        }
      });
    });

    return results.sort((a, b) => {
      if (sortConfig.key === "trigger") {
        return sortConfig.direction === "asc"
          ? a.trigger.localeCompare(b.trigger)
          : b.trigger.localeCompare(a.trigger);
      }
      let valA = a[sortConfig.key] || 0;
      let valB = b[sortConfig.key] || 0;
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [natalPositions, transitPositions, saPositions, sortConfig, selectedTrigger, pointerDegree]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <div style={{ opacity: 0.2, marginLeft: "4px" }}><RefreshCw size={10} /></div>;
    return sortConfig.direction === "asc" ? (
      <ChevronUp size={12} style={{ color: "#2c2d96", marginLeft: "4px" }} />
    ) : (
      <ChevronDown size={12} style={{ color: "#2c2d96", marginLeft: "4px" }} />
    );
  };


  const copyToClipboard = () => {
    const headers = "Trigger,P1,P2,Midpoint(360),90Deg,Signal,Modality,Orb,Meaning\n";
    const rows = filteredMidpoints.map(m => {
      const pair = m.p1 + "/" + m.p2;
      const mean = (MIDPOINT_AXES[pair] && MIDPOINT_AXES[pair].meaning) || "—";
      return `${m.tp},${m.p1},${m.p2},${m.displayLon.toFixed(2)},${m.mod90.toFixed(2)},${m.signal},${m.modality},${m.orb.toFixed(2)},"${mean}"`;
    }).join("\n");

    navigator.clipboard.writeText(headers + rows);
    alert("全ミッドポイント接触リストをクリップボードにコピーしました");
  };

  const downloadCSV = () => {
    const headers = "Trigger,P1,P2,Midpoint(360),90Deg,Signal,Modality,Orb,Meaning\n";
    const rows = filteredMidpoints.map(m => {
      const pair = m.p1 + "/" + m.p2;
      const mean = (MIDPOINT_AXES[pair] && MIDPOINT_AXES[pair].meaning) || "—";
      return `${m.tp},${m.p1},${m.p2},${m.displayLon.toFixed(2)},${m.mod90.toFixed(2)},${m.signal},${m.modality},${m.orb.toFixed(2)},"${mean}"`;
    }).join("\n");

    const blob = new Blob([headers + rows], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `midpoint_full_list_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadChartImage = () => {
    if (!svgRef.current) return;
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    const svgSize = svgRef.current.viewBox.baseVal;
    canvas.width = svgSize.width;
    canvas.height = svgSize.height;

    img.onload = () => {
      ctx.fillStyle = isDarkMode ? "#1a1b1e" : "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const getFormatDate = (label) => {
        if (!label) return "";
        const match = label.match(/(\d{4})\/(\d{2})\/(\d{2})/);
        return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
      };

      const bDate = getFormatDate(natalDateTime);
      const tDate = getFormatDate(transitDateTime);
      let filename = "midpoint";
      if (bDate) filename += `_${bDate}`;
      if (tDate) filename += `_${tDate}`;
      filename += ".png";

      const link = document.createElement("a");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    img.src =
      "data:image/svg+xml;charset=utf-8;base64," +
      btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(12, 1fr)",
        gap: isMobile ? "16px" : "24px",
        width: "100%",
      }}
    >
      {/* 1. 90度ダイアル */}
      <div
        style={Object.assign(
          {},
          commonStyles.dynamicBlockCard(isDarkMode, isMobile),
          {
            gridColumn: isMobile ? "span 12" : "span 7",
          },
        )}
      >
        <div
          style={Object.assign(
            {},
            commonStyles.dynamicBlockTitle(isDarkMode, isMobile),
            {
              color: isDarkMode ? "#fff" : "rgb(30, 41, 59)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            },
          )}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Compass size={20} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
            <span>90度ダイヤル</span>
          </div>
          <button
            onClick={downloadChartImage}
            style={{
              background: "none",
              border: "none",
              color: isDarkMode ? "#ffffff" : "#2c2d96",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            <Download size={18} /> Download Chart
          </button>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <svg
            ref={svgRef}
            viewBox="0 0 500 560"
            style={{
              width: "100%",
              maxWidth: "550px",
              background: isDarkMode ? "transparent" : "#fff",
            }}
          >
            <g>
              <text
                x="15"
                y="15"
                fontSize="11"
                fill={isDarkMode ? "#ffffff" : "#5f5f5f"}
                fontWeight="normal"
                textAnchor="start"
              >
                <tspan x="15" dy="0" fontWeight="bold">Inner Wheel</tspan>
                {assetName && <tspan x="15" dy="14" fontWeight="bold">{assetName}</tspan>}
                <tspan x="15" dy="14">Natal Chart</tspan>
                {natalDateTime ? (natalDateTime.split(' UTC').length > 1 ? (
                  <>
                    <tspan x="15" dy="14">{natalDateTime.split(' UTC')[0]}</tspan>
                    <tspan x="15" dy="14">UTC{natalDateTime.split(' UTC')[1]}</tspan>
                  </>
                ) : (
                  <tspan x="15" dy="14">{natalDateTime}</tspan>
                )) : null}
              </text>

              <text
                x={485}
                y="15"
                fontSize="11"
                fill={isDarkMode ? "#ffffff" : "#5f5f5f"}
                fontWeight="normal"
                textAnchor="end"
              >
                <tspan x={485} dy="0" fontWeight="bold">Outer Wheel</tspan>
                <tspan x={485} dy="14">Transit Chart</tspan>
                {transitDateTime ? (transitDateTime.split(' UTC').length > 1 ? (
                  <>
                    <tspan x={485} dy="14">{transitDateTime.split(' UTC')[0]}</tspan>
                    <tspan x={485} dy="14">UTC{transitDateTime.split(' UTC')[1]}</tspan>
                  </>
                ) : (
                  <tspan x={485} dy="14">{transitDateTime}</tspan>
                )) : null}
              </text>
            </g>
            <circle
              cx={centerX}
              cy={centerY}
              r={outerRadius}
              fill="none"
              stroke="#2c2d96"
              strokeWidth="1"
            />
            <circle
              cx={centerX}
              cy={centerY}
              r={innerRadius}
              fill="none"
              stroke="#8b5cf6"
              strokeWidth="1"
              strokeDasharray="4 4"
              opacity="0.4"
            />
            {/* 30度、60度のメイン区切り線 (活動・固定・柔軟) */}
            {[30, 60, 90].map((deg) => {
              const c_div1 = degToCoord(deg, outerRadius);
              const c_div2 = degToCoord(deg, outerRadius - 18);
              return (
                <line
                  key={"div-" + deg}
                  x1={c_div1.x}
                  y1={c_div1.y}
                  x2={c_div2.x}
                  y2={c_div2.y}
                  stroke={isDarkMode ? "#ffffff" : "#2c2d96"}
                  strokeWidth="2.5"
                />
              );
            })}

            {/* モダリティラベル */}
            {[
              { label: "CARDINAL (活動宮)", icon: "♈", deg: 15, color: QUALITY_STYLE.Cardinal.bg },
              { label: "FIXED (固定宮)", icon: "♉", deg: 45, color: QUALITY_STYLE.Fixed.bg },
              { label: "MUTABLE (柔軟宮)", icon: "♊", deg: 75, color: QUALITY_STYLE.Mutable.bg },
            ].map((m) => {
              const pos = degToCoord(m.deg, outerRadius + 12);
              const iconPos = degToCoord(m.deg, outerRadius + 32);
              return (
                <g key={m.label}>
                  <text
                    x={pos.x}
                    y={pos.y}
                    fontSize="10"
                    fontWeight="bold"
                    textAnchor="middle"
                    fill={m.color}
                    transform={`rotate(${m.deg * 4 - 90}, ${pos.x}, ${pos.y})`}
                  >
                    {m.label}
                  </text>
                </g>
              );
            })}

            {Array.from({ length: 90 }).map(function (_, i) {
              const isMajor = i % 15 === 0;
              const isDivision = i % 30 === 0;
              const c1 = degToCoord(i, outerRadius),
                c2 = degToCoord(i, outerRadius - (isDivision ? 18 : isMajor ? 12 : 6));
              return (
                <g key={i}>
                  <line
                    x1={c1.x}
                    y1={c1.y}
                    x2={c2.x}
                    y2={c2.y}
                    stroke={
                      isDivision
                        ? isDarkMode ? "#ffffff" : "#2c2d96"
                        : isMajor
                          ? isDarkMode ? "#ffffff" : "#8b5cf6"
                          : isDarkMode ? "rgba(255,255,255,0.2)" : "#cbd5e1"
                    }
                    strokeWidth={isDivision ? 2.5 : isMajor ? 1.5 : 0.5}
                  />
                  {isMajor && (
                    <text
                      x={degToCoord(i, outerRadius - 25).x}
                      y={degToCoord(i, outerRadius - 25).y}
                      fontSize="10"
                      textAnchor="middle"
                      fill={isDarkMode ? "#ffffff" : "#8b5cf6"}
                    >
                      {i}°
                    </text>
                  )}
                </g>
              );
            })}

            {/* ポインター（針） */}
            <g>
              {(() => {
                const c_tip = degToCoord(pointerDegree, outerRadius + 5);
                const c_center = { x: centerX, y: centerY };
                return (
                  <>
                    <line
                      x1={centerX}
                      y1={centerY}
                      x2={c_tip.x}
                      y2={c_tip.y}
                      stroke="#e74c3c"
                      strokeWidth="2"
                    />
                    <circle cx={centerX} cy={centerY} r="4" fill="#e74c3c" />
                    {/* 対極（45度先）の線 */}
                    {(() => {
                      const c_opp = degToCoord((pointerDegree + 45) % 90, outerRadius - 10);
                      return (
                        <line
                          x1={centerX}
                          y1={centerY}
                          x2={c_opp.x}
                          y2={c_opp.y}
                          stroke="#e74c3c"
                          strokeWidth="1"
                          strokeDasharray="4 2"
                          opacity="0.6"
                        />
                      );
                    })()}
                  </>
                );
              })()}
            </g>

            {filteredMidpoints.map(function (m, i) {
              if (m.orb > 1.5) return null;

              const p1Pos = m.p1 === "Aries" ? 0 : natalPositions[m.p1];
              const p2Pos = m.p2 === "Aries" ? 0 : natalPositions[m.p2];
              const tpPos = m.tp === "Aries" ? 0 : transitPositions[m.tp];

              if (
                p1Pos === undefined ||
                p2Pos === undefined ||
                tpPos === undefined
              )
                return null;

              const c1 = degToCoord(p1Pos % 90, innerRadius);
              const c2 = degToCoord(p2Pos % 90, innerRadius);
              const ct = degToCoord(tpPos % 90, outerRadius + 22);
              const ctOpposite = {
                x: centerX - (ct.x - centerX),
                y: centerY - (ct.y - centerY),
              };
              const midCoord = degToCoord(m.displayLon % 90, outerRadius - 15);
              const angle = (m.displayLon % 90) * 4;
              const isTight = m.orb <= 0.5;
              // ★修正: 色取得
              const color = getBodyColor(m.tp);

              return (
                <g key={"mid-visual-" + i}>
                  <line
                    x1={c1.x}
                    y1={c1.y}
                    x2={c2.x}
                    y2={c2.y}
                    stroke={color}
                    strokeWidth={isTight ? 1.2 : 0.6}
                    opacity={isTight ? 0.7 : 0.3}
                  />
                  <line
                    x1={ct.x}
                    y1={ct.y}
                    x2={ctOpposite.x}
                    y2={ctOpposite.y}
                    stroke={color}
                    strokeWidth={isTight ? 0.8 : 0.4}
                    strokeDasharray={isTight ? "" : "3 2"}
                    opacity={isTight ? 0.5 : 0.2}
                  />
                  <path
                    d="M -6 0 L 6 0 L 0 -10 Z"
                    fill={color}
                    opacity={isTight ? 1.0 : 0.6}
                    transform={
                      "translate(" +
                      midCoord.x +
                      "," +
                      midCoord.y +
                      ") rotate(" +
                      (angle + 180) +
                      ")"
                    }
                  />
                </g>
              );
            })}

            {natalBodies.map(function (b) {
              const p = b.id;
              const coord = degToCoord(b.exactLon % 90, b.displayRadius);
              const color = getBodyColor(p);

              return (
                <g key={"n-" + p}>
                  <circle
                    cx={coord.x}
                    cy={coord.y}
                    r="9"
                    fill="#fff"
                    stroke={color}
                    strokeWidth="1"
                    strokeDasharray="2 1"
                  />
                  <text
                    x={coord.x}
                    y={coord.y}
                    fontSize="9"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={color}
                  >
                    {p === "Aries" ? "♈\uFE0E" : GET_SYMBOL(p)}
                  </text>
                </g>
              );
            })}

            {transitBodies.map(function (b) {
              const p = b.id;
              const coord = degToCoord(b.exactLon % 90, b.displayRadius);
              const color = getBodyColor(p);

              return (
                <g key={"t-" + p}>
                  <circle
                    cx={coord.x}
                    cy={coord.y}
                    r="11"
                    fill="#fff"
                    stroke={color}
                    strokeWidth="2"
                  />
                  <text
                    x={coord.x}
                    y={coord.y}
                    fontSize="11"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={color}
                    fontWeight="bold"
                  >
                    {p === "Aries" ? "♈\uFE0E" : GET_SYMBOL(p)}
                  </text>
                </g>
              );
            })}
          </svg>
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              gap: "20px",
              fontSize: "12px",
              color: "#64748b",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: "#2c2d96",
                }}
              />{" "}
              Transit (Blue Solid)
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  border: "1px dashed #9c2393",
                }}
              />{" "}
              Natal (Purple Dashed)
            </span>
          </div>

          {/* POINTER AXIS 度数表示 */}
          <div style={{
            marginTop: "15px",
            padding: "12px 20px",
            background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
            borderRadius: "12px",
            border: "1px solid " + (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
            textAlign: "center",
            width: "fit-content",
            margin: "15px auto 0",
          }}>
            <div style={{ fontSize: "11px", color: "#888", letterSpacing: "1px", marginBottom: "2px" }}>POINTER AXIS</div>
            <div style={{ fontSize: "28px", fontWeight: "900", color: "#e74c3c" }}>{pointerDegree.toFixed(1)}°</div>
            <div style={{ fontSize: "11px", color: "#3498db" }}>+45°: {((pointerDegree + 45) % 90).toFixed(1)}°</div>
          </div>

          {/* 矢印キー操作パネル */}
          <div
            style={{
              marginTop: "12px",
              padding: "10px 15px",
              background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
              borderRadius: "12px",
              border: "1px solid " + (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: isDarkMode ? "#cbd5e1" : "#64748b",
              fontSize: "12px",
            }}
          >
            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
              <button
                onClick={() => setPointerDegree((prev) => parseFloat(((prev - 0.1 + 90) % 90).toFixed(1)))}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                title="-0.1°"
              >
                <ArrowLeft size={24} style={{ padding: "4px", background: isDarkMode ? "#333" : "#fff", borderRadius: "4px", border: "1px solid " + (isDarkMode ? "#555" : "#ddd"), color: isDarkMode ? "#fff" : "#2563eb" }} />
              </button>
              <button
                onClick={() => setPointerDegree((prev) => parseFloat(((prev + 0.1) % 90).toFixed(1)))}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                title="+0.1°"
              >
                <ArrowRight size={24} style={{ padding: "4px", background: isDarkMode ? "#333" : "#fff", borderRadius: "4px", border: "1px solid " + (isDarkMode ? "#555" : "#ddd"), color: isDarkMode ? "#fff" : "#2563eb" }} />
              </button>
            </div>
            <span style={{ fontWeight: "bold", marginLeft: "5px" }}>クリックまたは矢印キーでポインターを操作 (0.1°単位)</span>
          </div>
        </div>
      </div>

      {/* 2. 市場エネルギー指数詳細 */}
      <div
        style={Object.assign(
          {},
          commonStyles.dynamicBlockCard(isDarkMode, isMobile),
          {
            gridColumn: isMobile ? "span 12" : "span 5",
          },
        )}
      >
        <div
          style={Object.assign(
            {},
            commonStyles.dynamicBlockTitle(isDarkMode, isMobile),
            {
              color: isDarkMode ? "#fff" : "rgb(30, 41, 59)",
            },
          )}
        >
          <Zap
            size={20}
            color={isDarkMode ? "#ffffff" : "#f1c40f"}
            fill={isDarkMode ? "none" : "#f1c40f"}
          />{" "}
          <span>市場エネルギー指数</span>
        </div>
        <div
          style={{
            padding: isMobile ? "15px" : "24px",
            background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#fcfcfc",
            borderRadius: "15px",
            border: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "2px solid #2c2d96",
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              color: isDarkMode ? "#ffffff" : "#64748b",
              fontWeight: "bold",
            }}
          >
            合計エネルギー指数
          </div>
          <div
            style={{
              fontSize: isMobile ? "42px" : "56px",
              fontWeight: "900",
              color: isDarkMode ? "#ffffff" : "#2c2d96",
              margin: "4px 0",
            }}
          >
            {energyAnalysis.total}
          </div>
          <div
            style={{
              padding: "4px 16px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "bold",
              background: "#327aa3",
              display: "inline-block",
              color: "#fff",
            }}
          >
            {energyAnalysis.intensity}
          </div>
        </div>
        <div style={{ display: "grid", gap: "10px" }}>
          {[
            { label: "相場フェーズ", val: energyAnalysis.phase, color: "#333" },
            {
              label: "重要クラスタ数",
              val: clusters.length + " 箇所",
              color: "#c2410c",
            },
            {
              label: "主要トリガー",
              val: energyAnalysis.mainTrigger,
              color: "#27ae60",
            },
            {
              label: "優勢クオリティ",
              val: energyAnalysis.dominantQuality,
              color: "#555",
            },
          ].map(function (item, i) {
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "12px",
                  background: isDarkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "#f8f9ff",
                  borderRadius: "8px",
                  fontSize: isMobile ? "11px" : "12px",
                }}
              >
                <span
                  style={{
                    color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "#64748b",
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{
                    fontWeight: "bold",
                    color: isDarkMode ? "#ffffff" : item.color,
                  }}
                >
                  {item.val}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 5. ポインター軸のミッドポイント（自動計算結果） */}
      <div
        style={Object.assign(
          {},
          commonStyles.dynamicBlockCard(isDarkMode, isMobile),
          { gridColumn: "span 12", marginTop: "20px" },
        )}
      >
        <div
          style={Object.assign(
            {},
            commonStyles.dynamicBlockTitle(isDarkMode, isMobile),
            {
              color: isDarkMode ? "#fff" : "rgb(30, 41, 59)",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? "12px" : "0",
              width: "100%",
              paddingBottom: isMobile ? "10px" : "0"
            },
          )}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", width: isMobile ? "100%" : "auto" }}>
            <MousePointer2 size={18} />
            <span>
              {viewMode === "axis"
                ? "ポインター軸 (45°先: " + ((pointerDegree + 45) % 90).toFixed(1) + "°) のミッドポイント"
                : "トランジット・SA天体からのミッドポイント逆引き分析"
              }
            </span>
          </div>

          {/* タブ切り替えボタン */}
          <div style={{
            display: "flex",
            gap: isMobile ? "8px" : "12px",
            alignItems: isMobile ? "stretch" : "center",
            flexDirection: isMobile ? "column" : "row",
            width: isMobile ? "100%" : "auto"
          }}>
            {viewMode === "planet" && (
              <div style={{
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                gap: "8px",
                width: isMobile ? "100%" : "auto",
                flexDirection: isMobile ? "column" : "row"
              }}>
                <span style={{ fontSize: "12px", opacity: 0.8, fontWeight: "600", whiteSpace: "nowrap" }}>トリガー:</span>
                <select
                  value={selectedTrigger}
                  onChange={(e) => setSelectedTrigger(e.target.value)}
                  style={{
                    fontSize: "12px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    background: isDarkMode ? "#2d2d2d" : "#fff",
                    color: isDarkMode ? "#fff" : "#333",
                    border: "1px solid " + (isDarkMode ? "#444" : "#cbd5e1"),
                    outline: "none",
                    cursor: "pointer",
                    width: "100%",
                    maxWidth: isMobile ? "none" : "130px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                  }}
                >
                  <option value="all">All Hits</option>
                  <option value="Pointer">Pointer</option>
                  <optgroup label="Transit">
                    {Object.keys(transitPositions)
                      .filter(id => id !== "SouthNode" && !id.startsWith("H"))
                      .map(id => (
                        <option key={"T." + id} value={"T." + id}>{id}</option>
                      ))}
                  </optgroup>
                  <optgroup label="Solar Arc">
                    {Object.keys(saPositions)
                      .filter(id => id !== "SouthNode" && !id.startsWith("H"))
                      .map(id => (
                        <option key={"SA." + id} value={"SA." + id}>{id}</option>
                      ))}
                  </optgroup>
                </select>
              </div>
            )}
            <div style={{
              display: "flex",
              gap: "4px",
              background: isDarkMode ? "#1f1f1f" : "#f1f5f9",
              padding: "4px",
              borderRadius: "10px",
              width: isMobile ? "100%" : "auto",
              border: isDarkMode ? "1px solid #333" : "none",
              flexDirection: isMobile ? "column" : "row"
            }}>
              <button
                onClick={() => setViewMode("axis")}
                style={{
                  width: "100%",
                  padding: "8px 14px",
                  fontSize: "12px",
                  borderRadius: "8px",
                  border: "none",
                  background: viewMode === "axis" ? (isDarkMode ? "#fff" : "#2c2d96") : "transparent",
                  color: viewMode === "axis" ? (isDarkMode ? "#000" : "#fff") : (isDarkMode ? "#ccc" : "#64748b"),
                  cursor: "pointer",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s"
                }}
              >
                By Axis (ポインター軸)
              </button>
              <button
                onClick={() => setViewMode("planet")}
                style={{
                  width: "100%",
                  margin: isMobile ? "4px 0 0 0" : "0",
                  padding: "8px 14px",
                  fontSize: "12px",
                  borderRadius: "8px",
                  border: "none",
                  background: viewMode === "planet" ? (isDarkMode ? "#fff" : "#2c2d96") : "transparent",
                  color: viewMode === "planet" ? (isDarkMode ? "#000" : "#fff") : (isDarkMode ? "#ccc" : "#64748b"),
                  cursor: "pointer",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s"
                }}
              >
                By Planet (天体軸)
              </button>
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <div
            className="preserved-grid"
            style={{
              minWidth: isMobile ? (viewMode === "axis" ? "600px" : "500px") : "1100px",
              background: isDarkMode ? "transparent" : "#f1f5f9",
              display: "flex",
              flexDirection: "column",
              gap: "1px",
            }}
          >
            <div
              className="preserved-grid"
              style={{
                display: "grid",
                gridTemplateColumns: getMidpointGridLayout(isMobile, viewMode),
                padding: isMobile ? "12px 4px" : "14px 8px",
                background: isDarkMode
                  ? "rgba(255, 255, 255, 0.08)"
                  : "#f8fafc",
                fontSize: isMobile ? "10px" : "11px",
                fontWeight: "bold",
                color: isDarkMode ? "#ffffff" : "#64748b",
              }}
            >
              {viewMode === "axis" ? (
                <>
                  <div style={{ textAlign: "center" }}>{isMobile ? "P1" : "惑星1"}</div>
                  <div style={{ textAlign: "center" }}>{isMobile ? "P2" : "惑星2"}</div>
                  <div style={{ textAlign: "center" }}>ミッドポイント</div>
                  <div style={{ textAlign: "center", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleSort("displayLon")}>
                    90° <SortIcon column="displayLon" />
                  </div>
                  <div style={{ textAlign: "center" }}>T.接触</div>
                  <div style={{ textAlign: "center" }}>SA.接触</div>
                  <div style={{ textAlign: "center" }}>性質</div>
                  <div style={{ textAlign: "center", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleSort("orb")}>
                    AxisOrb <SortIcon column="orb" />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ textAlign: "center", cursor: "pointer" }} onClick={() => handleSort("trigger")}>トリガー<SortIcon column="trigger" /></div>
                  <div style={{ textAlign: "center" }}>ミッドポイント</div>
                  <div style={{ textAlign: "center", cursor: "pointer" }} onClick={() => handleSort("orb")}>Orb <SortIcon column="orb" /></div>
                  <div style={{ textAlign: "center" }}>Type</div>
                </>
              )}
              <div></div>
            </div>
            {(viewMode === "axis" ? pointerSelectedMidpoints : planetTriggeredMidpoints).map(function (m, i) {
              return (
                <MidpointRowItem
                  key={viewMode + "-" + i}
                  m={m}
                  isTight={m.orb <= 0.5}
                  planetColors={planetColors}
                  signColors={signColors}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  viewMode={viewMode}
                  natalPositions={natalPositions}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* 3. クラスタ性質分類 */}
      <div style={{ gridColumn: "span 12", marginTop: "10px" }}>
        <div
          style={Object.assign(
            {},
            commonStyles.dynamicBlockTitle(isDarkMode, isMobile),
            {
              marginBottom: "20px",
              color: isDarkMode ? "#fff" : "rgb(30, 41, 59)",
            },
          )}
        >
          <Layers size={20} />{" "}
          <span>ミッドポイント・クラスタ性質分類 (Cluster Analysis)</span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: isMobile ? "12px" : "20px",
          }}
        >
          {clusterClassifications.map(function (cls) {
            const type = cls.displayType;
            return (
              <div
                key={cls.id}
                style={Object.assign(
                  {},
                  commonStyles.dynamicBlockCard(isDarkMode, isMobile),
                  {
                    background: isDarkMode
                      ? "rgba(255, 255, 255, 0.03)"
                      : type.bg,
                    border: isDarkMode
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid " + type.border,
                    position: "relative",
                    overflow: "hidden",
                  },
                )}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: type.color,
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background: isDarkMode ? "rgba(255,255,255,0.1)" : "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                  >
                    {type.icon}
                  </div>
                  <span
                    style={{
                      fontWeight: "900",
                      fontSize: isMobile ? "15px" : "18px",
                    }}
                  >
                    {type.jpLabel} ({cls.status} Intensity)
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "900",
                    marginBottom: "8px",
                    color: isDarkMode ? "#ffffff" : "#1e293b",
                  }}
                >
                  {type.label}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: isDarkMode ? "rgba(255, 255, 255, 0.8)" : "#64748b",
                    lineHeight: "1.6",
                  }}
                >
                  {type.description}
                </p>
                <div
                  style={{
                    marginTop: "18px",
                    paddingTop: "12px",
                    borderTop: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid rgba(0,0,0,0.06)",
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: isMobile ? "12px" : "13px",
                    color: isDarkMode ? "#fff" : "#1e293b",
                  }}
                >
                  <span>
                    中心度数:{" "}
                    <strong style={{ fontSize: "14px" }}>
                      {cls.avgDeg.toFixed(2)}°
                    </strong>
                  </span>
                  <span>
                    構成数:{" "}
                    <strong style={{ fontSize: "14px" }}>
                      {cls.memberCount}
                    </strong>{" "}
                    midpoints
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>


      {/* ★修正: ModalityStripChart に signColors を渡す */}
      <ModalityStripChart
        transitDateTime={transitDateTime}
        isDarkMode={isDarkMode}
        planetColors={planetColors}
        signColors={signColors}
        isMobile={isMobile}
        isHeliocentric={isHeliocentric}
        natalPositions={natalPositions}
        // --- プロップス引き継ぎ ---
        initialSelectedTriggers={props.selectedTriggers}
        initialSelectedNatalBodies={props.selectedNatalBodies}
        orbFilter={props.orbFilter}
        userLocation={props.userLocation}
      />

      {/* 4. ミッドポイント接触リスト */}
      <div
        style={Object.assign(
          {},
          commonStyles.dynamicBlockCard(isDarkMode, isMobile),
          { gridColumn: "span 12" },
        )}
      >
        <div
          style={Object.assign(
            {},
            commonStyles.dynamicBlockTitle(isDarkMode, isMobile),
            {
              color: isDarkMode ? "#fff" : "rgb(30, 41, 59)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              flexWrap: "wrap",
              gap: "10px",
            },
          )}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Target size={18} />{" "}
            <span>ミッドポイント接触リスト ({filteredMidpoints.length}件)</span>
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={copyToClipboard}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
                background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
                border:
                  "1px solid " +
                  (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                color: isDarkMode ? "#cbd5e1" : "#64748b",
                cursor: "pointer",
              }}
            >
              <Copy size={14} />
              {!isMobile && "コピー"}
            </button>
            <button
              onClick={downloadCSV}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
                background: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
                border:
                  "1px solid " +
                  (isDarkMode ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
                color: isDarkMode ? "#cbd5e1" : "#64748b",
                cursor: "pointer",
              }}
            >
              <Database size={14} />
              {!isMobile && "CSV出力"}
            </button>
          </div>
        </div>

        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <div
            className="preserved-grid"
            style={{
              minWidth: isMobile ? "500px" : "1100px",
              background: isDarkMode ? "transparent" : "#f1f5f9",
              display: "flex",
              flexDirection: "column",
              gap: "1px",
            }}
          >
            <div
              className="preserved-grid"
              style={{
                display: "grid",
                gridTemplateColumns: getMidpointGridLayout(isMobile, "contact"),
                padding: isMobile ? "12px 4px" : "14px 8px",
                background: isDarkMode
                  ? "rgba(255, 255, 255, 0.08)"
                  : "#f8fafc",
                fontSize: isMobile ? "10px" : "11px",
                fontWeight: "bold",
                color: isDarkMode ? "#ffffff" : "#64748b",
              }}
            >
              <div style={{ textAlign: "center" }}>トリガー</div>
              <div style={{ textAlign: "center" }}>ミッドポイント</div>
              <div style={{ textAlign: "center", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleSort("displayLon")}>
                ミッドポイント<SortIcon column="displayLon" />
              </div>
              <div style={{ textAlign: "center", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleSort("mod90")}>
                90° <SortIcon column="mod90" />
              </div>
              <div style={{ textAlign: "center" }}>判定</div>
              <div style={{ textAlign: "center" }}>クラスタ</div>
              <div style={{ textAlign: "center" }}>性質</div>
              <div style={{ textAlign: "center", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => handleSort("orb")}>
                Orb <SortIcon column="orb" />
              </div>
              <div></div>
            </div>
            {filteredMidpoints.map(function (m, i) {
              return (
                <MidpointRowItem
                  key={i}
                  m={m}
                  isTight={m.orb <= 0.5}
                  isInCluster={clusters.some(function (c) {
                    return c.members.some(function (member) {
                      return member.p1 === m.p1 && member.p2 === m.p2 && member.tp === m.tp;
                    });
                  })}
                  planetColors={planetColors}
                  signColors={signColors}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  viewMode="contact"
                  natalPositions={natalPositions}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}